import {
  BulkInvestmentProposalRequest,
  BulkInvestmentProposalResponse,
  PaginatedInvestmentProposedUsersResponse,
  PaginatedUsersResponse,
  PropallyAgentV1Api,
} from "./generated/src";
import { BULK_MAX_TARGET_USER_COUNT } from "../constants/BulkProposal";
import { UserSearchEnvelopeRequestType } from "../types/UsersSearch";

// 送信対象の指定方法。チェック選択と、一覧の絞り込み条件そのものの2通り。
export type BulkProposalTarget =
  | { type: "user_ids"; userIds: number[] }
  | { type: "search"; search: UserSearchEnvelopeRequestType };

export interface BulkProposalParams {
  target: BulkProposalTarget;
  proposalContent: string;
  shortAppealMessage?: string;
  proposalDate?: string;
  agentMessageTemplateId?: number;
  // 提案済みユーザー向け（再提案）かどうか。対象ユーザーの母集団がサーバー側で切り替わる。
  isReProposal: boolean;
  // 同じ日（JST）に入っている既存の送信予約を取り消して置き換えるか。proposal_date が未来のときのみ作用する。
  replaceSameDay?: boolean;
}

// 一括送信は 1 リクエストで受け付けてもらい、実際の配信はサーバー側で順次行われる。
// ユーザー数分のリクエストを同時に投げるとタイムアウトで大量に失敗するため。
export const registerBulkProposals = async (params: BulkProposalParams): Promise<BulkInvestmentProposalResponse> => {
  const request = {
    target_type: params.target.type,
    proposal_content: params.proposalContent,
    short_appeal_message: params.shortAppealMessage,
    proposal_date: params.proposalDate,
    agent_message_template_id: params.agentMessageTemplateId,
    replace_same_day: params.replaceSameDay,
  } as BulkInvestmentProposalRequest;

  if (params.target.type === "user_ids") {
    request.user_ids = params.target.userIds;
  } else {
    request.search = params.target.search;
  }

  const api = new PropallyAgentV1Api();
  const post = params.isReProposal
    ? api.agentV1InvestmentProposalsBulkReDeliveriesPost.bind(api)
    : api.agentV1InvestmentProposalsBulkDeliveriesPost.bind(api);

  return new Promise((resolve, reject) => {
    post(
      { bulkInvestmentProposalRequest: request },
      (error: object, data: BulkInvestmentProposalResponse, response: object) => {
        if (error) {
          console.error(error);
          reject(error);
        } else {
          resolve(data);
        }
      }
    );
  });
};

// 送信前に対象が何名かを一覧と同じ検索APIで数える（件数だけ欲しいので per_page は 1）。
export const fetchConditionTargetCount = async (
  search: UserSearchEnvelopeRequestType,
  isReProposal: boolean
): Promise<number> => {
  const countRequest: UserSearchEnvelopeRequestType = {
    ...search,
    pagination: { page: 1, per_page: 1 },
  };
  const requestParams = { userSearchEnvelopeRequest: countRequest };
  const api = new PropallyAgentV1Api();
  const call = isReProposal
    ? api.agentV1InvestmentProposedUsersSearchPost.bind(api)
    : api.agentV1UsersSearchPost.bind(api);

  return new Promise((resolve, reject) => {
    call(
      requestParams,
      (
        error: object,
        data: PaginatedUsersResponse | PaginatedInvestmentProposedUsersResponse,
        response: object
      ) => {
        if (error) {
          console.error(error);
          reject(error);
        } else {
          resolve(data.total_count ?? 0);
        }
      }
    );
  });
};

// 一括送信の失敗を、次に何をすればよいか分かる文言へ変換する。
// サーバーの 400 message（上限超過など）は英語のため、そのまま出さずに判別して日本語化する。
// 500 系はチャンクコミットの途中失敗があり得るが、同じ内容の再送信で受付済みの対象は
// サーバー側が自動スキップするため、再試行を促す。
export const buildBulkErrorAlertText = (error: unknown): string => {
  const err = error as {
    status?: number;
    response?: { body?: { message?: string } };
    body?: { message?: string };
  };
  const message = err?.response?.body?.message ?? err?.body?.message;

  if (typeof message === "string" && (message.includes("exceeds") || message.includes("or fewer"))) {
    return `対象が上限（${BULK_MAX_TARGET_USER_COUNT.toLocaleString()}名）を超えているため送信できません。\n一覧で条件を追加して対象を絞ってください。`;
  }
  if ((err?.status ?? 0) >= 500) {
    return "エラーが発生しました。\n一部のみ受け付けられている可能性がありますが、同じ内容をもう一度送信すると受付済みの対象は自動的にスキップされます。\n時間をおいて再度お試しください。";
  }
  return "エラーが発生しました。\n時間をおいて再度お試しください。";
};

// 送信自体は受け付けただけで、ユーザーに届くのは順次になることを伝える。
// 全員が対象外だった場合は1件も登録されていないので、送信を示唆する文言を出さない。
export const buildBulkCompletionText = (result: BulkInvestmentProposalResponse): string => {
  const acceptedCount = result.accepted_count ?? 0;
  const skippedCount = result.skipped_count ?? 0;

  if (acceptedCount === 0) {
    // 「送信されなかった」と断定しない。クライアント側だけタイムアウトした後の再送など、
    // 1回目で登録済みのために全件スキップされているケースもあるため。
    const lines = ["送信対象として登録されたユーザーはいませんでした。"];
    if (skippedCount > 0) {
      // skipped_count には「本日提案済み」だけでなく、一覧が古くなって対象外になったユーザー
      // （既に提案済み・退会済みなど）も含まれる。明日なら送れるとは限らないので断定しない。
      lines.push(`${skippedCount}名は本日提案済み、または一覧の条件から外れたため対象外です。`);
    }
    return lines.join("\n");
  }

  const lines = [`${acceptedCount}名への送信を受け付けました。`, "順次送信されるため、反映まで数分かかることがあります。"];
  if (skippedCount > 0) {
    lines.push(`${skippedCount}名は本日提案済み、または一覧の条件から外れたため対象外です。`);
  }
  return lines.join("\n");
};
